import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
	CockpitFilter,
	CustomCockpitColumn,
	cockpitChartInterface,
	cockpitChartListInterface,
} from 'src/app/util/interfaces/cockpitRework/cockpitRework';
import { environment } from 'src/environments/environment';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { ChartData } from 'chart.js';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { DateService } from 'src/app/services/date/date.service';

interface CockpitFilterParameters {
	family?: string;
	period_type?: string;
	location?: string;
	locations?: string[];
	statuses?: Array<string | { status?: string }>;
	ciType?: string;
	subType?: string;
	ciTypesArray?: Array<{ ciType: number | string; subType: string | number }>;
	startDate?: string;
	endDate?: string;
	showInactiveCis?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class CockpitChartService {
	//  dateRange agora é substituído por period_type nos parâmetros
	getChartListUrl = environment.baseApiUrl + 'cockpit/indicator';
	getChartUrl = environment.baseApiUrl + 'cockpit/indicator/{idIndicator}?dateRange={dateRange}';
	deleteChartUrl = environment.baseApiUrl + 'cockpit/indicator/{idIndicator}';
	putCockpitIndicator = environment.baseApiUrl + 'cockpit/indicator/{idIndicator}';
	postFiltersUrl = environment.baseApiUrl + 'cockpit/user/filter';
	getCockpitFilter = environment.baseApiUrl + 'cockpit/filter';
	getCockpitSelectedFilters = environment.baseApiUrl + 'cockpit/selectedFilters';
	deleteCockpitFilters = environment.baseApiUrl + 'cockpit/deleteSelectedFilters';
	getCustomChartListUrl = environment.baseApiUrl + 'cockpit/customIndicator';
	getCockpitCustomFilterUrl = environment.baseApiUrl + 'cockpit/getCustomFilter';
	getCockpitLabelsUrl = environment.baseApiUrl + 'cockpit/getLabels';
	getCustomIndicatorUrl = environment.baseApiUrl + 'cockpit/getCustomIndicator';
	postCustomIndicatorUrl = environment.baseApiUrl + 'cockpit/createCustomIndicator';
	deleteCustomIndicatorUrl = environment.baseApiUrl + 'cockpit/deleteCustomIndicator/{idIndicator}';

	colorPickOptions = [
		'#F20D0D',
		'#F2800D',
		'#F2F20D',
		'#80F20D',
		'#0DF20D',
		'#0DF280',
		'#0DF2F2',
		'#0D80F2',
		'#0D0DF2',
		'#800DF2',
		'#F20DF2',
		'#F20D80',
	];

	constructor(
		private http: HttpClient,
		private AuthLoginService: AuthLoginService,
		private ProfileService: ProfileService,
		protected DateService: DateService
	) {}

	//Converte data de DD/MM/YY para YYYY-MM-DD
	private convertDDMMYYtoYYYYMMDD(dateString: string): string {
		const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
		const match = dateString.match(datePattern);

		if (match) {
			const day = match[1].padStart(2, '0');
			const month = match[2].padStart(2, '0');
			let year = match[3];

			if (year.length === 2) {
				const currentYear = new Date().getFullYear();
				const century = Math.floor(currentYear / 100) * 100;
				year = String(century + Number.parseInt(year));
			}

			return `${year}-${month}-${day}`;
		}

		return dateString;
	}

	getAvailableCharts(): Observable<cockpitChartListInterface[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.get<any>(this.getChartListUrl, { headers: headers }).pipe(
			map((chartListRes) => {
				const newChartList: cockpitChartListInterface[] = [];

				if (chartListRes && chartListRes.length > 0) {
					for (const item of chartListRes) {
						const newChartItem: cockpitChartListInterface = {
							id: item.id,
							label: item.label,
							name: item.name,
							type: item.type,
							id_customer: item.customer,
						};

						newChartList.push(newChartItem);
					}
				}

				newChartList.sort((a, b) => {
					if (a.label.toString() > b.label.toString()) return 1;
					if (a.label.toString() < b.label.toString()) return -1;
					return 0;
				});

				return newChartList;
			})
		);
	}

	getAvailableCustomCharts(): Observable<cockpitChartListInterface[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.get<any>(this.getCustomChartListUrl, { headers: headers }).pipe(
			map((chartListRes) => {
				const newChartList: cockpitChartListInterface[] = [];

				if (chartListRes && chartListRes.length > 0) {
					for (const item of chartListRes) {
						const newChartItem: cockpitChartListInterface = {
							id: item.id,
							label: item.label,
							name: item.name,
							type: item.type,
							id_customer: item.customer,
						};

						newChartList.push(newChartItem);
					}
				}

				newChartList.sort((a, b) => {
					if (a.label.toString() > b.label.toString()) return 1;
					if (a.label.toString() < b.label.toString()) return -1;
					return 0;
				});

				return newChartList;
			})
		);
	}

	private buildCockpitUrl(
		indicatorType?: string,
		startDate?: string,
		endDate?: string,
		filtersOverride?: CockpitFilterParameters
	): string {
		let getCockpitChartUrl = environment.baseApiUrl + 'cockpit?';
		let hasParameters = false;

		if (indicatorType) {
			getCockpitChartUrl += `indicatorType=${indicatorType}`;
			hasParameters = true;
		}

		const appendParameter = (key: string, value: string) => {
			if (!value) {
				return;
			}

			getCockpitChartUrl += `${hasParameters ? '&' : ''}${key}=${encodeURIComponent(value)}`;
			hasParameters = true;
		};

		const applyFilters = (filters: CockpitFilterParameters) => {
			if (filters.family) {
				appendParameter('family', filters.family);
			}

			if (filters.period_type) {
				appendParameter('period_type', filters.period_type);
			}

			const filterStartDate = filters.startDate || startDate;
			const filterEndDate = filters.endDate || endDate;
			if (filterStartDate && filterEndDate) {
				appendParameter('custom_date', `${filterStartDate}-${filterEndDate}`);
			}

			if (filters.locations && Array.isArray(filters.locations) && filters.locations.length > 0) {
				appendParameter('location', filters.locations.join(','));
			} else if (filters.location) {
				appendParameter('location', filters.location);
			}

			if (filters.statuses && Array.isArray(filters.statuses) && filters.statuses.length > 0) {
				const statusesString = filters.statuses
					.map((item) => (typeof item === 'object' && item.status ? item.status : item))
					.filter((item) => !!item)
					.join(',');
				appendParameter('status', statusesString);
			}

			if (filters.ciTypesArray && Array.isArray(filters.ciTypesArray) && filters.ciTypesArray.length > 0) {
				const ciTypesWithoutSubType = filters.ciTypesArray
					.filter((item) => item.subType === '' || item.subType === null || item.subType === undefined)
					.map((item) => item.ciType);

				if (ciTypesWithoutSubType.length > 0) {
					appendParameter('ciType', ciTypesWithoutSubType.join(','));
				}

				const subTypesArray = filters.ciTypesArray
					.filter((item) => item.subType !== '' && item.subType !== null && item.subType !== undefined)
					.map((item) => item.subType);

				if (subTypesArray.length > 0) {
					appendParameter('subtype', subTypesArray.join(','));
				}
			} else if (filters.ciType) {
				appendParameter('ciType', filters.ciType);

				if (filters.subType) {
					appendParameter('subtype', filters.subType);
				}
			}

			if (filters.showInactiveCis !== undefined) {
				appendParameter('showActiveOnlyCis', String(!filters.showInactiveCis));
			}
		};

		if (filtersOverride) {
			applyFilters(filtersOverride);
		} else {
			const cachedFilters = localStorage.getItem('cockpitFilters');
			if (cachedFilters) {
				try {
					applyFilters(JSON.parse(cachedFilters));
				} catch (error) {
					console.error('Erro ao ler cockpitFilters:', error);
				}
			} else if (startDate && endDate) {
				appendParameter('custom_date', `${startDate}-${endDate}`);
			}
		}

		return getCockpitChartUrl;
	}

	getCockpitByFilters(filters: CockpitFilterParameters, indicatorType?: string): Observable<cockpitChartInterface[]> {
		return this.getCockpit(indicatorType, undefined, undefined, filters);
	}

	getCockpit(
		indicatorType?: string,
		startDate?: string,
		endDate?: string,
		filtersOverride?: CockpitFilterParameters
	): Observable<cockpitChartInterface[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});

		const getCockpitChartUrl = this.buildCockpitUrl(indicatorType, startDate, endDate, filtersOverride);

		return this.http.get<any>(getCockpitChartUrl, { headers: headers }).pipe(
			map((cockpitAnswer) => {
				const documentStyle = document.documentElement.style;

				const newCockpitChartArray: cockpitChartInterface[] = [];
				if (!cockpitAnswer || !cockpitAnswer.indicators || cockpitAnswer.indicators.length === 0) return newCockpitChartArray;

				for (const chart of cockpitAnswer.indicators) {
					const documentStyle = document.documentElement.style;

					const newCockpitChart: cockpitChartInterface = {
						id: chart.id,
						title: chart.name,
						title_label: chart.label,
						type: chart.type,
						availableDrillDown: chart.availableDrillDown,
						availableReports: chart.availableReports,
						options: chart.type + 'ChartOptions',
						chartData: null,
						multiple: chart.multiple,
						custom: indicatorType === 'custom' ? true : false,
						family: chart.family,
						drillDownIndex: chart.drillDownIndex || '',
					};

					if (chart.values && chart.values.length > 0) {
						const newChartData: ChartData = {
							datasets: [],
							labels: [],
						};

						// Tratamento especial para tipo 'flow' - converte string JSON para formato padrão
						if (chart.type === 'flow' && typeof chart.values[0] === 'string') {
							try {
								const parsedData = JSON.parse(chart.values[0]);
								// Transforma o formato flow em formato padrão
								chart.values = parsedData.datasets.map((dataset) => ({
									data: dataset.data,
									labels: parsedData.labels.map((label) =>
										this.DateService.formatDate(this.convertDDMMYYtoYYYYMMDD(label.toString()))
									),
									label: dataset.label,
								}));
								chart.type = 'bar';
								// Atualiza também o newCockpitChart
								newCockpitChart.type = 'bar';
								newCockpitChart.options = 'barChartOptions';
							} catch (error) {
								console.error('Erro ao fazer parse dos dados do gráfico flow:', error);
							}
						}

						//Looping through datasets, mostly will be just one
						for (let index = 0; index < chart.values.length; index++) {
							const newData: ChartData['datasets'][number] = {
								data: [],
								backgroundColor: [] as string[],
								borderColor: [] as string[],
								pointBackgroundColor: [] as string[],
								pointBorderColor: [] as string[],
								label: '',
							};

							//Looping through data and labels inside a dataset
							if (chart.values[index].data && chart.values[index].data.length > 0) {
								newChartData.labels = chart.values[index].labels;

								if (chart.multiple || chart.values[index].label) {
									newData.label = chart.values[index].label;
								}

								const colorArray = [];
								for (let k = 0; k < chart.values[index].data.length; k++) {
									const sourceColor =
										Math.random() <= 0.5
											? this.ProfileService.getUserStylesheet()['primary-color-1']
											: this.ProfileService.getUserStylesheet()['primary-color-2'];
									colorArray.push(this.generateColor(sourceColor, 1, 40));
								}
								newData.backgroundColor = colorArray;
								newData.hoverBackgroundColor = colorArray;
								// newData.borderColor = [this.ProfileService.getUserStylesheet().primaryColor1];
								// newData.pointBackgroundColor = [this.ProfileService.getUserStylesheet().primaryColor1];
								// newData.pointBorderColor = [this.ProfileService.getUserStylesheet().primaryColor1];

								for (let index_ = 0; index_ < chart.values[index].data.length; index_++) {
									newData.data.push(chart.values[index].data[index_] === '' ? 0 : chart.values[index].data[index_]);
								}
							}

							newChartData.datasets.push(<undefined>newData);
						}

						newCockpitChart.chartData = newChartData;
					}

					if (chart.type === 'text') {
						newCockpitChart.chartData = chart.values[0].value;
					}

					newCockpitChartArray.push(newCockpitChart);
				}

				return newCockpitChartArray;
			})
		);
	}

	getCockpitList(): Observable<cockpitChartListInterface[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		const getCockpitChartUrl = environment.baseApiUrl + 'cockpit';
		return this.http.get<any>(getCockpitChartUrl, { headers: headers }).pipe(
			map((cockpit) => {
				const newCockpitList: cockpitChartListInterface[] = [];

				if (cockpit && cockpit.indicators && cockpit.indicators.length > 0) {
					for (const item of cockpit.indicators) {
						if (item.type != 'text') {
							const newCockpit: cockpitChartListInterface = {
								id: item.id,
								id_customer: 0,
								name: item.name,
								type: '',
								label: item.label,
							};

							newCockpitList.push(newCockpit);
						}
					}
				}

				return newCockpitList;
			})
		);
	}

	getCustomCockpitList(indicatorType: string): Observable<cockpitChartListInterface[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		// dateRange numérico (0,1,2) - agora usa period_type do cockpitFilters
		// let allowedDateRange = ['0','1','2'];
		// let dateRange = ( localStorage.getItem('ckpL') && allowedDateRange.includes(localStorage.getItem('ckpL')) ) ? localStorage.getItem('ckpL') : '0';
		// let getCockpitChartUrl = this.getCockpitChartUrl.replace('{dateRange}', '0') + `&indicatorType=${indicatorType}`; // Deprecated

		const getCockpitChartUrl = environment.baseApiUrl + 'cockpit?indicatorType=' + indicatorType;

		return this.http.get<any>(getCockpitChartUrl, { headers: headers }).pipe(
			map((cockpit) => {
				const newCockpitList: cockpitChartListInterface[] = [];

				if (cockpit && cockpit.indicators && cockpit.indicators.length > 0) {
					for (const item of cockpit.indicators) {
						if (item.type != 'text') {
							const newCockpit: cockpitChartListInterface = {
								id: item.id,
								id_customer: 0,
								name: item.name,
								type: '',
								label: item.label,
							};

							newCockpitList.push(newCockpit);
						}
					}
				}

				return newCockpitList;
			})
		);
	}

	getChartById(idIndicator: string | number, indicatorType?: string): Observable<cockpitChartInterface> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		//  dateRange numérico (0,1,2,3) - agora usa period_type e datas do cockpitFilters
		// let allowedDateRange = ['0','1','2','3'];
		// let dateRange = ( localStorage.getItem('ckpL') && allowedDateRange.includes(localStorage.getItem('ckpL')) ) ? localStorage.getItem('ckpL') : '0';
		// let getChartUrl = this.getChartUrl.replace('{idIndicator}', idIndicator.toString()).replace('{dateRange}', '0'); // Deprecated

		let getChartUrl = environment.baseApiUrl + 'cockpit/indicator/' + idIndicator.toString() + '?';
		let hasParameters = false;

		if (indicatorType) {
			getChartUrl += `indicatorType=${indicatorType}`;
			hasParameters = true;
		}

		// Lê os filtros do cockpitFilters do localStorage
		const cachedFilters = localStorage.getItem('cockpitFilters');
		if (cachedFilters) {
			try {
				const filters = JSON.parse(cachedFilters);

				if (filters.period_type && filters.period_type !== '') {
					getChartUrl += `${hasParameters ? '&' : ''}period_type=${filters.period_type}`;
					hasParameters = true;
				}

				if (filters.startDate && filters.endDate) {
					getChartUrl += `${hasParameters ? '&' : ''}custom_date=${filters.startDate}-${filters.endDate}`;
					hasParameters = true;
				}

				if (filters.location && filters.location !== '') {
					getChartUrl += `${hasParameters ? '&' : ''}location=${filters.location}`;
					hasParameters = true;
				}

				if (filters.ciType && filters.ciType !== '') {
					getChartUrl += `${hasParameters ? '&' : ''}ciType=${filters.ciType}`;
					hasParameters = true;
				}

				if (filters.subType && filters.subType !== '') {
					getChartUrl += `${hasParameters ? '&' : ''}subtype=${filters.subType}`;
					hasParameters = true;
				}
			} catch (error) {
				console.error('Erro ao ler cockpitFilters:', error);
			}
		}

		//  lógica antiga com customDate
		// if(dateRange === '3') {
		//   let stringCustomDate = localStorage.getItem('customDate');
		//   let objetoCustomDate = JSON.parse(stringCustomDate);
		//   getChartUrl += `&startDate=${objetoCustomDate.startDate}&endDate=${objetoCustomDate.endDate}`;
		// }

		return this.http.get<any>(getChartUrl, { headers: headers }).pipe(
			map((chart) => {
				const documentStyle = document.documentElement.style;

				const newCockpitChart: cockpitChartInterface = {
					id: chart.id,
					title: chart.name,
					title_label: chart.label,
					type: chart.type,
					availableDrillDown: chart.availableDrillDown,
					availableReports: chart.availableReports,
					options: chart.type + 'ChartOptions',
					chartData: null,
					multiple: chart.multiple,
					drillDownIndex: chart.drillDownIndex || '',
				};

				if (chart.values && chart.values.length > 0) {
					// Tratamento especial para tipo 'flow' - converte string JSON para formato padrão
					if (chart.type === 'flow' && typeof chart.values[0] === 'string') {
						try {
							const parsedData = JSON.parse(chart.values[0]);
							// Transforma o formato flow em formato padrão
							chart.values = parsedData.datasets.map((dataset) => ({
								data: dataset.data,
								labels: parsedData.labels,
								label: dataset.label,
							}));
							chart.type = 'bar';
							// Atualiza também o newCockpitChart
							newCockpitChart.type = 'bar';
							newCockpitChart.options = 'barChartOptions';
						} catch (error) {
							console.error('Erro ao fazer parse dos dados do gráfico flow:', error);
						}
					}

					const newChartData: ChartData = {
						datasets: [],
						labels: [],
					};

					//Looping through datasets, mostly will be just one
					for (let index = 0; index < chart.values.length; index++) {
						const newData: ChartData['datasets'][number] = {
							data: [],
							backgroundColor: [] as string[],
							borderColor: [] as string[],
							pointBackgroundColor: [] as string[],
							pointBorderColor: [] as string[],
							label: '',
						};

						//Looping through data and labels inside a dataset
						if (chart.values[index].data && chart.values[index].data.length > 0) {
							newChartData.labels = chart.values[index].labels;

							if (chart.multiple) {
								newData.label = chart.values[index].label;
							}

							const colorArray = [];
							for (let k = 0; k < chart.values[index].data.length; k++) {
								const sourceColor =
									Math.random() <= 0.5
										? this.ProfileService.getUserStylesheet()['primary-color-1']
										: this.ProfileService.getUserStylesheet()['primary-color-2'];
								colorArray.push(this.generateColor(sourceColor, 1, 40));
							}
							newData.backgroundColor = colorArray;
							newData.hoverBackgroundColor = colorArray;
							// newData.borderColor = this.ProfileService.getUserStylesheet().primaryColor1;
							// newData.pointBackgroundColor = this.ProfileService.getUserStylesheet().primaryColor1;
							// newData.pointBorderColor = this.ProfileService.getUserStylesheet().primaryColor1;

							for (let index_ = 0; index_ < chart.values[index].data.length; index_++) {
								newData.data.push(chart.values[index].data[index_] === '' ? 0 : chart.values[index].data[index_]);
							}
						}

						newChartData.datasets.push(<undefined>newData);
					}

					newCockpitChart.chartData = newChartData;
				}

				if (chart.type === 'text') {
					newCockpitChart.chartData = chart.values[0].value;
				}

				return newCockpitChart;
			})
		);
	}

	addChart(idIndicator: string, indicatorType?: string): Observable<any> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		let putCockpitIndicator = this.putCockpitIndicator.replace('{idIndicator}', idIndicator);
		if (indicatorType) {
			putCockpitIndicator += `?indicatorType=${indicatorType}`;
		}

		return this.http.put(putCockpitIndicator, {}, { headers: headers });
	}

	deleteChartById(idIndicator: string, indicatorType?: string): Observable<any> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		let deleteChartUrl = this.deleteChartUrl.replace('{idIndicator}', idIndicator);
		if (indicatorType) {
			deleteChartUrl += `?indicatorType=${indicatorType}`;
		}

		return this.http.delete(deleteChartUrl, { headers: headers });
	}

	postCustomIndicator(data: CustomCockpitColumn[]): Observable<any> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		const body = {
			type: data[0].type,
			column_name: data[0].COLUMN_NAME,
			indicator_name: data[0].indicatorName,
			label: data[0].label,
			interval: data[0].interval,
			param_value: data[0].param_value,
		};

		return this.http.post(this.postCustomIndicatorUrl, body, { headers: headers });
	}

	getCustomFilter(): Observable<CustomCockpitColumn[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.get<JSON[]>(this.getCockpitCustomFilterUrl, { headers: headers }).pipe(
			map((column) => {
				const newColumn: CustomCockpitColumn[] = [];
				column.forEach((element: any) => {
					const newColumnCustom: CustomCockpitColumn = {
						COLUMN_NAME: element.COLUMN_NAME,
					};
					newColumn.push(newColumnCustom);
				});
				return newColumn;
			})
		);
	}

	getLabels(): Observable<CustomCockpitColumn[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.get<JSON[]>(this.getCockpitLabelsUrl, { headers: headers }).pipe(
			map((label) => {
				const newLabel: CustomCockpitColumn[] = [];
				label.forEach((element: any) => {
					const newLabelCustom: CustomCockpitColumn = {
						label: element.label,
					};
					newLabel.push(newLabelCustom);
				});
				return newLabel;
			})
		);
	}

	getAllCustomIndicator(): Observable<CustomCockpitColumn[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.get<JSON[]>(this.getCustomIndicatorUrl, { headers: headers }).pipe(
			map((indicator) => {
				const newCustomIndicator: CustomCockpitColumn[] = [];
				indicator.forEach((element: any) => {
					const newIndicator: CustomCockpitColumn = {
						idIndicator: element.idIndicator,
						type: element.type,
						indicatorName: element.indicatorName,
					};
					newCustomIndicator.push(newIndicator);
				});
				return newCustomIndicator;
			})
		);
	}

	deleteCustomIndicator(idIndicator: string): Observable<any> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		const deleteCustomIndicatorUrl = this.deleteCustomIndicatorUrl.replace('{idIndicator}', idIndicator);
		return this.http.delete(deleteCustomIndicatorUrl, { headers: headers });
	}

	getFilter(): Observable<CockpitFilter[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});

		return this.http.get<JSON[]>(this.getCockpitFilter, { headers: headers }).pipe(
			map((filter) => {
				const newFilter: CockpitFilter[] = [];

				filter.forEach((element: any) => {
					const newFilterItem: CockpitFilter = {
						indicatorFamily: element.indicatorFamily,
						location: element.location,
						site: element.site,
						solutionArea: element.solutionArea,
						scope: element.scope,
						department: element.department,
						customer: element.customer,
					};
					newFilter.push(newFilterItem);
				});

				return newFilter;
			})
		);
	}

	getSelectedFilters(): Observable<CockpitFilter[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});

		return this.http.get<JSON[]>(this.getCockpitSelectedFilters, { headers: headers }).pipe(
			map((filter) => {
				const newFilter: CockpitFilter[] = [];

				filter.forEach((element: any) => {
					const newFilterItem: CockpitFilter = {
						indicatorFamily: element.family,
						location: element.location,
						site: element.site,
						solutionArea: element.solutionArea,
						scope: element.scope,
						department: element.department,
						customer: element.customer,
					};
					newFilter.push(newFilterItem);
				});

				return newFilter;
			})
		);
	}

	postFilter(filter: string): Observable<any> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.post(this.postFiltersUrl, filter, { headers: headers });
	}

	deleteFilters(): Observable<any> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		return this.http.delete(this.deleteCockpitFilters, { headers: headers });
	}

	private getRandomColor(availableColors: string[]): string {
		const randomIndex = Math.floor(Math.random() * availableColors.length);
		return availableColors[randomIndex];
	}

	generateColor(sourceHex: string, quantity: number, variance = 20): string[] {
		// Remove o "#" se presente
		sourceHex = sourceHex.replace('#', '');

		// Converte hex para RGB
		const r = Number.parseInt(sourceHex.slice(0, 2), 16);
		const g = Number.parseInt(sourceHex.slice(2, 4), 16);
		const b = Number.parseInt(sourceHex.slice(4, 6), 16);

		const colorArray = [];

		for (let index = 0; index < quantity; index++) {
			const newR = this.clamp(r + this.randomValue(variance), 0, 255);
			const newG = this.clamp(g + this.randomValue(variance), 0, 255);
			const newB = this.clamp(b + this.randomValue(variance), 0, 255);

			const newColor = this.rgbToHex(newR, newG, newB);
			colorArray.push(newColor);
		}

		return colorArray;
	}

	randomValue(variance: number) {
		return Math.floor(Math.random() * (2 * variance + 1)) - variance;
	}

	clamp(valor: number, min: number, max: number) {
		return Math.min(Math.max(valor, min), max);
	}

	rgbToHex(r: number, g: number, b: number) {
		return (
			'#' +
			[r, g, b]
				.map((x) => x.toString(16).padStart(2, '0'))
				.join('')
				.toUpperCase()
		);
	}
}
